
import {
    useLayoutEffect,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    FlatList,
    Image,
    View
} from "react-native";
import type {
    SearchCompanyType,
    SearchMemberType
} from "./type";
import stylesheet from "./stylesheet";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    TextInput,
    CheckBox,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    type RouteProp,
    useNavigation,
    useIsFocused,
    useRoute
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    CompainesSearchObjectType,
    UsersSearchObjectType,
    GetSearchRequestType
} from "../../../services/restAPI/actions/getSearch/types";
import type {
    SectorsObjectType
} from "../../../services/restAPI/actions/getSectors/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    AppTabParamList
} from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRight,
    ListTodo,
    Search
} from "lucide-react-native";
import {
    RESTService
} from "../../../services/restAPI";

const SearchPage = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
    const route = useRoute<RouteProp<AppTabParamList, "SearchTab">>();

    const searchTextParam = route.params?.searchTextParam;
    const searchTypeParam = route.params?.searchTypeParam;

    const isFocused = useIsFocused();
    const isFirstMount = useRef(true);

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        searchLoading,
        setSearchLoading
    ] = useState(false);

    const [
        userSearchData,
        setUserSearchData
    ] = useState<Array<UsersSearchObjectType>>([]);

    const [
        companySearchData,
        setCompanySearchData
    ] = useState<Array<CompainesSearchObjectType>>([]);

    const [
        searchText,
        setSearchText
    ] = useState<string>(searchTextParam ? searchTextParam : "");

    const [
        page,
        setPage
    ] = useState(1);

    const [
        endReached,
        setEndReached
    ] = useState(false);

    const [
        searchType,
        setSearchType
    ] = useState<"user" | "company">(searchTypeParam ? searchTypeParam : "user");

    const [
        sectorsData,
        setSectorsData
    ] = useState<Array<SectorsObjectType>>([]);

    const [
        selectedSectorIDs,
        setSelectedSectorIDs
    ] = useState<Array<string>>([]);

    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const inset = useSafeAreaInsets();

    useEffect(() => {
        getSectors();
        if(searchTextParam) {
            getSearchData({
                searchType: searchTypeParam ? searchTypeParam : "user",
                searchText: searchTextParam
            });
        } else {
            getSearchData({
                searchType: searchTypeParam ? searchTypeParam : "user"
            });
        }
    }, []);

    useEffect(() => {
        if (searchTypeParam) {
            setSearchType(searchTypeParam);

            const _params: GetSearchRequestType = {
                searchType: searchTypeParam
            };

            if(searchText && searchText.length) {
                _params.searchText = searchText;
            }

            if(selectedSectorIDs && selectedSectorIDs.length) {
                _params.sectorIDs = selectedSectorIDs;
            }

            setPage(1);
            setEndReached(false);
            getSearchData(_params);

            navigation.setParams({
                searchTypeParam: undefined
            });
        }
    }, [searchTypeParam]);

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }

        if (isFocused && !searchTypeParam) {
            const _params: GetSearchRequestType = {
                searchType: searchType
            };

            if(searchText && searchText.length) {
                _params.searchText = searchText;
            }

            if(selectedSectorIDs && selectedSectorIDs.length) {
                _params.sectorIDs = selectedSectorIDs;
            }

            setPage(1);
            setEndReached(false);
            getSearchData(_params);
        }
    }, [isFocused]);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => renderSearchHeader()
        });
    }, [
        navigation,
        searchType,
        searchText,
        selectedSectorIDs
    ]);

    useEffect(() => {
        setPage(1);
        setEndReached(false);
        getSearchData();
    }, [selectedSectorIDs]);

    const getSearchData = (searchParams?: GetSearchRequestType) => {
        setSearchLoading(true);

        const params: GetSearchRequestType = {
            searchType: searchType
        };

        if(selectedSectorIDs && selectedSectorIDs.length) {
            params.sectorIDs = selectedSectorIDs;
        }

        if(searchText && searchText.length) {
            params.searchText = searchText;
        }

        if(searchParams) {
            if(searchParams.page) {
                params.page = searchParams.page;
            }

            if(searchParams.searchText) {
                params.searchText = searchParams.searchText;
            }

            if(searchParams.searchType) {
                params.searchType = searchParams.searchType;
            }

            if(searchParams.sectorIDs && searchParams.sectorIDs.length) {
                params.sectorIDs = searchParams.sectorIDs;
            }
        }

        RESTService.action("GetSearch", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(params.searchType === "user") {
                        setCompanySearchData([]);
                        if(searchParams && searchParams.page) {
                            setPage(searchParams.page);
                            const _userSearchData = JSON.parse(JSON.stringify(userSearchData));
                            const newUserSearchDAta = res.payload.users.filter(newItem => !_userSearchData.some((oldItem: { userID: string }) => oldItem.userID === newItem.userID));
                            setUserSearchData([
                                ..._userSearchData,
                                ...newUserSearchDAta
                            ]);
                        } else {
                            setUserSearchData(res.payload.users);
                        }

                        if(res.payload.users.length < 20) {
                            setEndReached(true);
                        }
                    }

                    if(params.searchType === "company") {
                        setUserSearchData([]);
                        if(searchParams && searchParams.page) {
                            setPage(searchParams.page);
                            const _companySearchData = JSON.parse(JSON.stringify(companySearchData));
                            const newCompanySearchData = res.payload.companies.filter(newItem => !_companySearchData.some((oldItem: { companyID: string }) => oldItem.companyID === newItem.companyID));
                            setCompanySearchData([
                                ..._companySearchData,
                                ...newCompanySearchData
                            ]);
                        } else {
                            setCompanySearchData(res.payload.companies);
                        }
                        if(res.payload.companies.length < 20) {
                            setEndReached(true);
                        }
                    }
                    setSearchLoading(false);
                }
            }).catch((err) => {
                console.log(err);
            }).finally(() => {
                setSearchLoading(false);
            });
    };

    const getSectors = () => {
        RESTService.action("GetSectors", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSectorsData(res.payload);
                }

                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const renderSearchHeader = () => {
        return <View
            style={{
                backgroundColor: colors.content.container.mid,
                paddingTop: spaces.spacingMd + inset.top
            }}
        >
            <View
                style={{
                    ...stylesheet.headerContainer,
                    paddingHorizontal: spaces.spacingMd,
                    marginBottom: spaces.spacingMd
                }}
            >
                <TextInput
                    customColor={colors.content.container.subtle}
                    spreadBehaviour="stretch"
                    placeholder={localize("searchPlaceholder")}
                    variant="text"
                    defaultValue={searchText}
                    onChangeText={(e) => {
                        setSearchText(e);
                        setPage(1);
                        setEndReached(false);
                        const params: GetSearchRequestType = {
                            searchText: e,
                            searchType: searchType
                        };

                        if(selectedSectorIDs && selectedSectorIDs.length) {
                            params.sectorIDs = selectedSectorIDs;
                        }

                        getSearchData(params);
                    }}
                    onSubmitEditing={() => {
                        setPage(1);
                        setEndReached(false);
                        const params: GetSearchRequestType = {
                            searchText: searchText,
                            searchType: searchType
                        };
                        if(selectedSectorIDs && selectedSectorIDs.length) {
                            params.sectorIDs = selectedSectorIDs;
                        }
                        getSearchData(params);
                    }}
                    icon={() => <Search
                        size={12}
                        color={colors.content.icon.low}
                    />}
                    style={{
                        marginRight: spaces.spacingMd
                    }}
                />
                <TouchableOpacity
                    style={{
                        padding: spaces.spacingMd,
                        backgroundColor: colors.content.container.subtle,
                        borderRadius: radiuses.sm
                    }}
                    onPress={() => {
                        bottomSheetRef.current?.open();
                    }}
                >
                    <ListTodo
                        size={20}
                        color={colors.content.icon.mid}
                    />
                </TouchableOpacity>
            </View>

            {selectedSectorIDs && selectedSectorIDs.length > 0 && <View
                style={{
                    marginBottom: spaces.spacingMd,
                    paddingLeft: spaces.spacingMd
                }}
            >
                <ScrollView
                    showsHorizontalScrollIndicator={false}
                    horizontal={true}
                >
                    {selectedSectorIDs.map(id => {
                        const _sector = sectorsData.find(d => d._id === id);

                        if(!_sector) {
                            return null;
                        }

                        return <Chip
                            onPress={() => {
                                const _selectedSectorIDs: Array<string> = JSON.parse(JSON.stringify(selectedSectorIDs));

                                const idx = _selectedSectorIDs.findIndex(e => e === id);

                                if(idx !== -1) {
                                    _selectedSectorIDs.splice(idx, 1);

                                    setSelectedSectorIDs(_selectedSectorIDs);
                                }
                            }}
                            title={_sector.value}
                            isClosable={true}
                            type="warning"
                            style={{
                                marginRight: spaces.spacingSm
                            }}
                            key={id}
                        />;
                    })}
                </ScrollView>
            </View>}

            <View
                style={{
                    ...stylesheet.headerInputContainer,
                    paddingHorizontal: spaces.spacingMd,
                    marginBottom: spaces.spacingMd
                }}
            >
                <TouchableOpacity
                    style={{
                        paddingHorizontal: spaces.spacingXl,
                        marginRight: spaces.spacingMd,
                        paddingVertical: spaces.spacingSm,
                        borderBottomWidth: searchType === "company" ? borders.line : 0,
                        borderBottomColor: colors.content.border.warning
                    }}
                    onPress={() => {
                        if(searchType === "company") {
                            return;
                        }
                        setSearchType("company");
                        const _params: GetSearchRequestType = {
                            searchType: "company"
                        };
                        if(searchText && searchText.length) {
                            _params.searchText = searchText;
                        }
                        if(selectedSectorIDs && selectedSectorIDs.length) {
                            _params.sectorIDs = selectedSectorIDs;
                        }
                        setPage(1);
                        setEndReached(false);
                        getSearchData(_params);
                    }}
                >
                    <Text
                        variant="bodyLargeSize"
                        color={searchType === "company" ? "warning" : "mid"}
                    >
                        {localize("companies")}
                    </Text>
                </TouchableOpacity>
                {renderSearchBorder()}
                <TouchableOpacity
                    style={{
                        marginLeft: spaces.spacingMd,
                        paddingHorizontal: spaces.spacingXl,
                        paddingVertical: spaces.spacingSm,
                        borderBottomWidth: searchType === "user" ? borders.line : 0,
                        borderBottomColor: colors.content.border.warning
                    }}
                    onPress={() => {
                        if(searchType === "user") {
                            return;
                        }
                        setSearchType("user");
                        const _params: GetSearchRequestType = {
                            searchType: "user"
                        };
                        if(searchText && searchText.length) {
                            _params.searchText = searchText;
                        }
                        if(selectedSectorIDs && selectedSectorIDs.length) {
                            _params.sectorIDs = selectedSectorIDs;
                        }
                        setPage(1);
                        setEndReached(false);
                        getSearchData(_params);
                    }}
                >
                    <Text
                        variant="bodyLargeSize"
                        color={searchType === "user" ? "warning" : "mid"}
                    >
                        {localize("members")}
                    </Text>
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderSearchBorder = () => {
        return <View
            style={{
                borderLeftColor: colors.content.border.subtle,
                borderLeftWidth:borders.line,
                height: spaces.spacingLg
            }}
        />;
    };

    const renderCompanyCard = (company: SearchCompanyType) => {
        return <TouchableOpacity
            onPress={() => {
                navigation.navigate("CompanyDetail", {
                    companyID: company.id
                });
            }}
            style={{
                backgroundColor: colors.content.container.mid,
                paddingHorizontal: spaces.spacingMd,
                ...stylesheet.companyCardContainer,
                paddingVertical: spaces.spacingMd,
                marginBottom: spaces.spacingMd,
                borderRadius: radiuses.actions
            }}
        >
            <Image
                source={{
                    uri: company.imageURL
                }}
                resizeMode="contain"
                style={[
                    stylesheet.cardImage,
                    {
                        marginRight: spaces.spacingMd,
                        borderRadius: radiuses.md,
                        backgroundColor: colors.content.container.default,
                        borderWidth: borders.line,
                        borderColor: colors.content.border.subtle
                    }
                ]}
            />

            <View
                style={{
                    ...stylesheet.companyContent,
                    marginRight: spaces.spacingSm
                }}
            >
                <Text
                    variant="titleMediumSize"
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                >
                    {company.title}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    numberOfLines={3}
                    color="mid"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {company.address}
                </Text>

                <ScrollView
                    showsHorizontalScrollIndicator={false}
                    nestedScrollEnabled={true}
                    style={stylesheet.width100}
                    horizontal={true}
                >
                    {company.sectors.map(e => {
                        const _sector = sectorsData.find(d =>  d._id === e);

                        return  <View
                            key={e}
                        >
                            {renderSectorChip(_sector?.value)}
                        </View>;
                    })}
                </ScrollView>
            </View>

            <View
                style={{
                    ...stylesheet.companyNavigation,
                    padding: spaces.spacingSm
                }}
            >
                <ChevronRight/>
            </View>
        </TouchableOpacity>;
    };

    const renderMemberCard = (member:SearchMemberType) => {
        return <TouchableOpacity
            onPress={() => {
                navigation.navigate("UserProfile", {
                    userID: member.id
                });
            }}
            style={{
                backgroundColor: colors.content.container.mid,
                paddingHorizontal: spaces.spacingMd,
                paddingVertical: spaces.spacingMd,
                ...stylesheet.memberCardContainer,
                marginBottom: spaces.spacingMd,
                borderRadius: radiuses.actions
            }}
        >
            <Image
                source={{
                    uri: member.imageURL
                }}
                resizeMode="cover"
                style={[
                    stylesheet.cardImage,
                    {
                        marginRight: spaces.spacingMd,
                        borderRadius: radiuses.md
                    }
                ]}
            />

            <View
                style={{
                    ...stylesheet.memberCardContent,
                    marginRight: spaces.spacingSm
                }}
            >
                <Text
                    variant="titleMediumSize"
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                >
                    {member.name}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    numberOfLines={3}
                    color="mid"
                    style={{
                        marginBottom: spaces.spacingSm
                    }}
                >
                    {member.position || localize("notSpecified")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    numberOfLines={3}
                    color="mid"
                >
                    {member.company || localize("notSpecified")}
                </Text>
            </View>

            <View
                style={{
                    ...stylesheet.memberCardNavigation,
                    padding: spaces.spacingSm
                }}
            >
                <ChevronRight/>
            </View>
        </TouchableOpacity>;
    };

    const renderSectorChip = (sector?: string) => {
        return <View
            style={[
                stylesheet.sectorChipContainer,
                {
                    backgroundColor: colors.content.container.warning,
                    paddingHorizontal: spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    marginBottom: spaces.spacingSm,
                    marginRight: spaces.spacingSm,
                    borderRadius: radiuses.xl
                }
            ]}
        >
            <Text
                variant="labelMediumSize"
                color="warning"
            >
                {sector}
            </Text>
        </View>;
    };

    const renderCompanyList = () => {
        if(searchType === "user") {
            return null;
        }

        return <FlatList
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.companyID}
            onEndReached={() => {
                if(endReached) {
                    return;
                }
                if(searchLoading) {
                    return;
                }
                getSearchData({
                    page: page + 1,
                    searchType: searchType
                });
            }}
            onEndReachedThreshold={0.7}
            renderItem={({
                item
            }) => {
                return renderCompanyCard({
                    address: item.address,
                    id: item.companyID,
                    imageURL: item.logoURL,
                    sectors: item.sectors,
                    title: item.companyTitle
                });
            }}
            style={[
                stylesheet.flex1,
                {
                    marginBottom: spaces.spacingXl * 4
                }
            ]}
            horizontal={false}
            data={companySearchData}
        />;
    };

    const renderUserList = () => {
        if(searchType === "company") {
            return null;
        }

        return <FlatList
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            keyExtractor={(item) => item.userID}
            onEndReached={() => {
                if(endReached) {
                    return;
                }
                if(searchLoading) {
                    return;
                }
                getSearchData({
                    page: page + 1,
                    searchType: searchType
                });
            }}
            onEndReachedThreshold={0.7}
            renderItem={({
                item
            }) => {
                return renderMemberCard({
                    company: item.companies[0]?.companyTitle || localize("notSpecified"),
                    imageURL: item.profilePhotoURL,
                    id: item.userID,
                    name: item.fullName,
                    position: item.companies[0]?.positionTitle || localize("notSpecified")
                });
            }}
            style={[
                stylesheet.flex1,
                {
                    marginBottom: spaces.spacingXl * 4
                }
            ]}
            horizontal={false}
            data={userSearchData}
        />;
    };

    const renderSectorSheet = () => {
        return <BottomSheet
            ref={bottomSheetRef}
            renderHeader={() => {
                return <View
                    style={[
                        stylesheet.sheetHeaderContainer,
                        {
                            padding: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="headlineSmallSize"
                        color="high"
                    >
                        {localize("sectors")}
                    </Text>
                </View>;
            }}
            isCanFullScreenOnSwipe={false}
            isWorkAsFullScreen={true}
            style={{
                borderTopRightRadius: radiuses.xl,
                borderTopLeftRadius: radiuses.xl
            }}
        >
            <ScrollView
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                nestedScrollEnabled={true}
                style={[
                    stylesheet.flex1,
                    {
                        marginBottom: spaces.spacingXl * 4
                    }
                ]}
            >
                {sectorsData.map((item) => {
                    let isChecked: "checked" | "partially" | null | undefined = null;

                    const isSelected = selectedSectorIDs.findIndex(e => e === item._id);

                    if(isSelected !== -1) {
                        isChecked = "checked";
                    }

                    return <CheckBox
                        key={item._id}
                        title={item.value}
                        isChecked={isChecked}
                        onPress={() => {
                            const _selectedSectorIDs: Array<string> = JSON.parse(JSON.stringify(selectedSectorIDs));

                            const isAlreadyExist = _selectedSectorIDs.findIndex(e => e === item._id);

                            if(isAlreadyExist !== -1) {
                                _selectedSectorIDs?.splice(isAlreadyExist, 1);

                                setSelectedSectorIDs(_selectedSectorIDs);
                            } else {
                                _selectedSectorIDs.push(item._id);

                                setSelectedSectorIDs(_selectedSectorIDs);
                            }

                        }}
                    />;
                })}
            </ScrollView>
        </BottomSheet>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="subtle"
        isScrollable={false}
    >
        {renderCompanyList()}
        {renderUserList()}
        {renderSectorSheet()}
    </PageContainer>;
};
export default SearchPage;
